import React from 'react';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { AdminSymbolFilterMode, AdminSymbolMappingStatus } from '../../api/admin';

interface DateFilterValue {
	from: string;
	to: string;
	mode: AdminSymbolFilterMode;
}

interface CompanyFilterValue {
	value: string;
	mode: AdminSymbolFilterMode;
}

function FilterHeader({
	label,
	active,
	onOpen,
}: {
	label: string;
	active: boolean;
	onOpen: (event: React.MouseEvent<HTMLElement>) => void;
}) {
	return (
		<Stack direction="row" alignItems="center" spacing={0.25} sx={{ minWidth: 0 }}>
			<Typography variant="body2" fontWeight={500} noWrap>{label}</Typography>
			<Tooltip title={`Filter ${label}`}>
				<IconButton
					size="small"
					color={active ? 'primary' : 'default'}
					onClick={onOpen}
					aria-label={`Filter ${label}`}
				>
					<FilterAltIcon fontSize="small" />
				</IconButton>
			</Tooltip>
		</Stack>
	);
}

function FilterModeToggle({
	value,
	onChange,
}: {
	value: AdminSymbolFilterMode;
	onChange: (value: AdminSymbolFilterMode) => void;
}) {
	return (
		<ToggleButtonGroup
			exclusive
			fullWidth
			size="small"
			value={value}
			onChange={(_, nextValue: AdminSymbolFilterMode | null) => {
				if (nextValue) onChange(nextValue);
			}}
			aria-label="Filter mode"
		>
			<ToggleButton value="include">Include</ToggleButton>
			<ToggleButton value="exclude">Exclude</ToggleButton>
		</ToggleButtonGroup>
	);
}

function FilterActions({
	applyDisabled,
	onClear,
	onCancel,
	onApply,
}: {
	applyDisabled?: boolean;
	onClear: () => void;
	onCancel: () => void;
	onApply: () => void;
}) {
	return (
		<Stack direction="row" justifyContent="space-between">
			<Button size="small" color="inherit" onClick={onClear}>Clear</Button>
			<Stack direction="row" spacing={1}>
				<Button size="small" color="inherit" onClick={onCancel}>Cancel</Button>
				<Button
					size="small"
					variant="contained"
					disabled={applyDisabled}
					onClick={onApply}
				>
					Apply
				</Button>
			</Stack>
		</Stack>
	);
}
// Filter popovers and the DS Type Select menu render into Modal portals. React
// bubbles their synthetic events back through this component's position in the
// React tree, i.e. into the DataGrid column header cell. The header's own
// portal guard (GridColumnHeaderItem -> event.currentTarget.contains(event.target))
// throws "Argument 1 does not implement interface Node" in Firefox when such an
// event carries a non-DOM-Node target (portal/focus-trap synthesized events).
// Stop only those malformed events here; genuine clicks keep propagating so
// MUI's portal guard, click-away, and focus behavior stay intact.
// `display: contents` keeps the wrapper layout-neutral inside the header cell.
export function PopoverEventGuard({ children }: { children: React.ReactNode }) {
	const stopNonNodeTargetEvent = (event: React.SyntheticEvent) => {
		if (!(event.target instanceof Node)) {
			event.stopPropagation();
		}
	};

	return (
		<Box
			display="contents"
			onClick={stopNonNodeTargetEvent}
			onDoubleClick={stopNonNodeTargetEvent}
			onMouseDown={stopNonNodeTargetEvent}
			onMouseOver={stopNonNodeTargetEvent}
			onMouseOut={stopNonNodeTargetEvent}
			onKeyDown={stopNonNodeTargetEvent}
			onFocus={stopNonNodeTargetEvent}
			onBlur={stopNonNodeTargetEvent}
		>
			{children}
		</Box>
	);
}

export function SymbolsDateColumnFilter({
	label,
	value,
	onApply,
}: {
	label: string;
	value: DateFilterValue;
	onApply: (value: DateFilterValue) => void;
}) {
	const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
	const [draft, setDraft] = React.useState(value);
	const invalidRange = Boolean(draft.from && draft.to && draft.from > draft.to);

	const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		setDraft(value);
		setAnchorEl(event.currentTarget);
	};

	return (
		<PopoverEventGuard>
			<FilterHeader
				label={label}
				active={Boolean(value.from || value.to)}
				onOpen={handleOpen}
			/>
			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			>
				<Stack spacing={1.5} sx={{ p: 2, width: 320 }}>
					<Typography variant="subtitle2">Filter {label}</Typography>
					<FilterModeToggle
						value={draft.mode}
						onChange={(mode) => setDraft((current) => ({ ...current, mode }))}
					/>
					<Stack direction="row" spacing={1}>
						<TextField
							label="From"
							type="date"
							value={draft.from}
							onChange={(event) => setDraft((current) => ({
								...current,
								from: event.target.value,
							}))}
							InputLabelProps={{ shrink: true }}
							error={invalidRange}
							fullWidth
						/>
						<TextField
							label="To"
							type="date"
							value={draft.to}
							onChange={(event) => setDraft((current) => ({
								...current,
								to: event.target.value,
							}))}
							InputLabelProps={{ shrink: true }}
							error={invalidRange}
							fullWidth
						/>
					</Stack>
					{invalidRange && (
						<Typography variant="caption" color="error.main">
							From must not be after To.
						</Typography>
					)}
					<FilterActions
						applyDisabled={invalidRange}
						onClear={() => {
							onApply({ from: '', to: '', mode: 'include' });
							setAnchorEl(null);
						}}
						onCancel={() => setAnchorEl(null)}
						onApply={() => {
							if (invalidRange) return;
							onApply(draft);
							setAnchorEl(null);
						}}
					/>
				</Stack>
			</Popover>
		</PopoverEventGuard>
	);
}

export function SymbolsCompanyColumnFilter({
	label,
	value,
	onApply,
}: {
	label: string;
	value: CompanyFilterValue;
	onApply: (value: CompanyFilterValue) => void;
}) {
	const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
	const [draft, setDraft] = React.useState(value);

	const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		setDraft(value);
		setAnchorEl(event.currentTarget);
	};

	return (
		<PopoverEventGuard>
			<FilterHeader
				label={label}
				active={Boolean(value.value.trim())}
				onOpen={handleOpen}
			/>
			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			>
				<Stack spacing={1.5} sx={{ p: 2, width: 320 }}>
					<Typography variant="subtitle2">Filter {label}</Typography>
					<FilterModeToggle
						value={draft.mode}
						onChange={(mode) => setDraft((current) => ({ ...current, mode }))}
					/>
					<TextField
						label="Company name contains"
						value={draft.value}
						onChange={(event) => setDraft((current) => ({
							...current,
							value: event.target.value,
						}))}
						inputProps={{ maxLength: 200 }}
						fullWidth
					/>
					<FilterActions
						onClear={() => {
							onApply({ value: '', mode: 'include' });
							setAnchorEl(null);
						}}
						onCancel={() => setAnchorEl(null)}
						onApply={() => {
							onApply(draft);
							setAnchorEl(null);
						}}
					/>
				</Stack>
			</Popover>
		</PopoverEventGuard>
	);
}

const MAPPING_STATUS_OPTIONS: Array<{ value: AdminSymbolMappingStatus; label: string }> = [
	{ value: 'any', label: 'Any' },
	{ value: 'active', label: 'Active' },
	{ value: 'inactive', label: 'Inactive' },
	{ value: 'unmapped', label: 'Unmapped' },
];

export function SymbolsMappingStatusColumnFilter({
	label,
	value,
	onApply,
}: {
	label: string;
	value: AdminSymbolMappingStatus;
	onApply: (value: AdminSymbolMappingStatus) => void;
}) {
	const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
	const [draft, setDraft] = React.useState(value);

	const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		setDraft(value);
		setAnchorEl(event.currentTarget);
	};

	return (
		<PopoverEventGuard>
			<FilterHeader
				label={label}
				active={value !== 'any'}
				onOpen={handleOpen}
			/>
			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			>
				<Stack spacing={1.5} sx={{ p: 2, width: 320 }}>
					<Typography variant="subtitle2">Filter {label}</Typography>
					<ToggleButtonGroup
						exclusive
						fullWidth
						size="small"
						value={draft}
						onChange={(_, nextValue: AdminSymbolMappingStatus | null) => {
							if (nextValue) setDraft(nextValue);
						}}
						aria-label="Mapping status"
					>
						{MAPPING_STATUS_OPTIONS.map((option) => (
							<ToggleButton key={option.value} value={option.value}>
								{option.label}
							</ToggleButton>
						))}
					</ToggleButtonGroup>
					<FilterActions
						onClear={() => {
							onApply('any');
							setAnchorEl(null);
						}}
						onCancel={() => setAnchorEl(null)}
						onApply={() => {
							onApply(draft);
							setAnchorEl(null);
						}}
					/>
				</Stack>
			</Popover>
		</PopoverEventGuard>
	);
}

export function SymbolsDsTypeColumnFilter({
	label,
	value,
	options,
	onApply,
}: {
	label: string;
	value: string[];
	options: string[];
	onApply: (value: string[]) => void;
}) {
	const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
	const [draft, setDraft] = React.useState(value);

	const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		setDraft(value.filter((item) => options.includes(item)));
		setAnchorEl(event.currentTarget);
	};

	return (
		<PopoverEventGuard>
			<FilterHeader
				label={label}
				active={value.length > 0}
				onOpen={handleOpen}
			/>
			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			>
				<Stack spacing={1.5} sx={{ p: 2, width: 320 }}>
					<Typography variant="subtitle2">Filter {label}</Typography>
					<FormControl fullWidth size="small" disabled={options.length === 0}>
						<InputLabel id="symbols-ds-type-filter-label">{label}</InputLabel>
						<Select<string[]>
							labelId="symbols-ds-type-filter-label"
							multiple
							value={draft}
							label={label}
							onChange={(event) => {
								const nextValue = event.target.value;
								setDraft(typeof nextValue === 'string' ? nextValue.split(',') : nextValue);
							}}
							renderValue={(selected) => selected.join(', ')}
						>
							{options.map((option) => (
								<MenuItem key={option} value={option}>
									<Checkbox checked={draft.includes(option)} size="small" />
									<ListItemText primary={option} />
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<FilterActions
						onClear={() => {
							onApply([]);
							setAnchorEl(null);
						}}
						onCancel={() => setAnchorEl(null)}
						onApply={() => {
							onApply(draft.filter((item) => options.includes(item)));
							setAnchorEl(null);
						}}
					/>
				</Stack>
			</Popover>
		</PopoverEventGuard>
	);
}
